import { useCallback, useRef, useSyncExternalStore } from "react";

// Tiny localStorage-backed store, shared across every usePersistedState(key, ...)
const cache = {};
const listeners = {};

const readValue = (key, defaultValue) => {
  if (key in cache) return cache[key];
  try {
    const raw = localStorage.getItem(key);
    cache[key] = raw != null ? JSON.parse(raw) : defaultValue;
  } catch {
    cache[key] = defaultValue;
  }
  return cache[key];
};

const writeValue = (key, value) => {
  cache[key] = value;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage-quota/serialization errors - the in-memory cache still
  }
  (listeners[key] || []).forEach((listener) => listener());
};

const subscribe = (key, listener) => {
  listeners[key] = listeners[key] || [];
  listeners[key].push(listener);
  return () => {
    listeners[key] = listeners[key].filter((l) => l !== listener);
  };
};

export const setPersistedState = (key, value) => writeValue(key, value);

// Test-only helper: usePersistedState caches the last known value per key in
export const __clearPersistedStateCacheForTests = (key) => {
  if (key) {
    delete cache[key];
  } else {
    Object.keys(cache).forEach((k) => delete cache[k]);
  }
};

export const usePersistedState = (key, defaultValue) => {
  const defaultRef = useRef(defaultValue);

  const getSnapshot = useCallback(() => readValue(key, defaultRef.current), [key]);
  const subscribeToKey = useCallback((listener) => subscribe(key, listener), [key]);

  const value = useSyncExternalStore(subscribeToKey, getSnapshot, getSnapshot);

  const setValue = useCallback(
    (nextValue) => {
      const current = readValue(key, defaultRef.current);
      const resolved = typeof nextValue === "function" ? nextValue(current) : nextValue;
      writeValue(key, resolved);
    },
    [key]
  );

  return [value, setValue];
};
